import React, { useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { apiService, LiveKnowledgeSearchResult } from '../services/apiService';
import KnowledgeResultCard from './KnowledgeResultCard';
import { Button } from './ui/button';
import { Dialog } from './ui/dialog';
import Spinner from './ui/Spinner';

type KnowledgeType = LiveKnowledgeSearchResult['type'];

/** One type's search for the current query: waiting for its answer, answered, or failed. */
type TypeSearch =
  | { status: 'loading' }
  | { status: 'loaded'; results: LiveKnowledgeSearchResult[]; isPartial: boolean }
  | { status: 'failed' };

/** The result groups, in the order the panel lists them, with their names. Same on macOS and WPF. */
const KNOWLEDGE_TYPES: { type: KnowledgeType; one: string; many: string; title: string }[] = [
  { type: 'objection', one: 'objection', many: 'objections', title: 'Objections' },
  { type: 'faq', one: 'FAQ', many: 'FAQs', title: 'FAQs' },
  { type: 'landmine', one: 'landmine', many: 'landmines', title: 'Landmines' },
  { type: 'competitor', one: 'competitor', many: 'competitors', title: 'Competitors' },
];

export const LIVE_KNOWLEDGE_SEARCH_COPY = {
  button: 'Search KB',
  title: 'Knowledge Search',
  placeholder: 'Search objections, FAQs, landmines, competitors',
  tooShort: 'Enter at least two characters to search.',
  /** The pending types, named in group order: "Searching landmines, competitors…". */
  searching: (names: string) => `Searching ${names}…`,
  unavailable: 'Knowledge search is temporarily unavailable.',
  noResults: 'No matching guidance found.',
  partial: 'Some Mongo results were omitted because this search reached its result limit.',
} as const;

/** A group's header: "2 objections found", "1 FAQ found". */
export const knowledgeFoundLabel = (type: KnowledgeType, count: number) => {
  const names = KNOWLEDGE_TYPES.find((entry) => entry.type === type);
  return `${count} ${count === 1 ? names?.one : names?.many} found`;
};

// A source that was down: its types were not searched, the rest were. Same copy on macOS and WPF.
const unavailableKnowledgeNote = (types: KnowledgeType[]) => {
  const names = KNOWLEDGE_TYPES.filter((entry) => types.includes(entry.type)).map((entry) => entry.title);
  return `Some knowledge could not be searched right now: ${names.join(', ')}.`;
};

/**
 * Search KB: a button that opens Knowledge Search. Typing searches as you go, one
 * request per type, and each type's answers show as soon as they arrive, counted
 * ("2 objections found", "1 landmine found"); a click on a group shows its answers,
 * drawn as the live call draws them. The same button and modal as macOS and WPF.
 */
const LiveKnowledgeSearch: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      {/* The macOS small bordered button: white, 1px border, radius 6, about 22 tall */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex h-[22px] flex-shrink-0 items-center gap-1 rounded-inner border border-ink-border bg-surface px-2 text-xs font-medium text-ink transition-colors hover:bg-surface-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-signature"
        aria-label="Search knowledge base"
      >
        <SearchIcon sx={{ fontSize: 11 }} />
        <span>{LIVE_KNOWLEDGE_SEARCH_COPY.button}</span>
      </button>
      {/* Mounted only while open, so every search starts fresh. */}
      {isOpen && <KnowledgeSearchModal onClose={() => setIsOpen(false)} />}
    </>
  );
};

const KnowledgeSearchModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [query, setQuery] = useState('');
  // Each type's search for the latest query; empty until a search starts.
  const [searches, setSearches] = useState<Partial<Record<KnowledgeType, TypeSearch>>>({});
  const [openGroups, setOpenGroups] = useState<KnowledgeType[]>([]);
  const trimmedQuery = query.trim();
  const isSearchable = trimmedQuery.length >= 2;

  // Escape closes, as a click outside does.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Searches as the rep types (debounced), one request per type in parallel, so a
  // slow source does not hold up the others. A newer query starts over: an older
  // query's late answers are dropped, never mixed in.
  useEffect(() => {
    if (!isSearchable) {
      setSearches({});
      setOpenGroups([]);
      return undefined;
    }

    let isCurrent = true;
    const settle = (type: KnowledgeType, search: TypeSearch) => {
      if (isCurrent) setSearches((current) => ({ ...current, [type]: search }));
    };
    const timer = window.setTimeout(() => {
      setSearches(KNOWLEDGE_TYPES.reduce<Partial<Record<KnowledgeType, TypeSearch>>>(
        (all, { type }) => ({ ...all, [type]: { status: 'loading' } }),
        {},
      ));
      // A new query folds the groups; a type arriving later for it leaves them as the rep set them.
      setOpenGroups([]);
      KNOWLEDGE_TYPES.forEach(({ type }) => {
        apiService.searchLiveKnowledge(trimmedQuery, type)
          .then((searchResponse) => settle(type, searchResponse.unavailableTypes.includes(type)
            ? { status: 'failed' }
            : {
              status: 'loaded',
              results: searchResponse.results.filter((result) => result.type === type),
              isPartial: searchResponse.isPartial,
            }))
          .catch(() => settle(type, { status: 'failed' }));
      });
    }, 250);

    return () => {
      isCurrent = false;
      window.clearTimeout(timer);
    };
  }, [trimmedQuery, isSearchable]);

  const typeSearches = isSearchable
    ? KNOWLEDGE_TYPES.map((entry) => ({ ...entry, search: searches[entry.type] }))
    : [];
  const pending = typeSearches.filter(({ search }) => search?.status === 'loading');
  const failedTypes = typeSearches.filter(({ search }) => search?.status === 'failed').map(({ type }) => type);
  const loaded = typeSearches.flatMap(({ search }) => (search?.status === 'loaded' ? [search] : []));
  const groups = typeSearches.flatMap((entry) => (
    entry.search?.status === 'loaded' && entry.search.results.length > 0
      ? [{ ...entry, results: entry.search.results }]
      : []
  ));
  const isAllFailed = failedTypes.length === KNOWLEDGE_TYPES.length;
  const isFinished = typeSearches.length > 0 && typeSearches.every(({ search }) => search && search.status !== 'loading');

  const toggleGroup = (type: KnowledgeType) => setOpenGroups((open) => (
    open.includes(type) ? open.filter((value) => value !== type) : [...open, type]
  ));

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }} widthClassName="max-w-2xl">
      <div role="dialog" aria-modal="true" aria-label={LIVE_KNOWLEDGE_SEARCH_COPY.title}>
        {/* The macOS sheet: paper, padding 20, 12 between the title row, the field and the results */}
        <div className="modal-panel flex max-h-[80vh] flex-col bg-paper p-5">
          <div className="mb-3 flex items-center justify-between gap-4">
            <h2 className="text-[17px] font-semibold text-ink">{LIVE_KNOWLEDGE_SEARCH_COPY.title}</h2>
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>Close</Button>
          </div>

          <label className="sr-only" htmlFor="live-knowledge-query">Search knowledge base</label>
          <input
            id="live-knowledge-query"
            type="search"
            value={query}
            autoFocus
            autoComplete="off"
            onChange={(event) => setQuery(event.target.value)}
            placeholder={LIVE_KNOWLEDGE_SEARCH_COPY.placeholder}
            className="h-10 w-full rounded-control border border-ink-border bg-surface px-3 text-sm text-ink placeholder:text-ink-muted focus:border-signature focus:outline-none focus:ring-2 focus:ring-signature"
          />

          <div aria-live="polite" className="mt-3 min-h-0 flex-1 overflow-y-auto">
            {!isSearchable && <p className="text-sm text-ink-muted">{LIVE_KNOWLEDGE_SEARCH_COPY.tooShort}</p>}
            {isAllFailed && <p role="alert" className="py-1 text-sm text-error">{LIVE_KNOWLEDGE_SEARCH_COPY.unavailable}</p>}
            {isFinished && loaded.length > 0 && groups.length === 0 && (
              <p className="py-1 text-sm text-ink-muted">{LIVE_KNOWLEDGE_SEARCH_COPY.noResults}</p>
            )}
            {loaded.some((search) => search.isPartial) && <p className="pb-2 text-xs text-ink-muted">{LIVE_KNOWLEDGE_SEARCH_COPY.partial}</p>}
            {failedTypes.length > 0 && !isAllFailed && (
              <p className="pb-2 text-xs text-ink-muted">{unavailableKnowledgeNote(failedTypes)}</p>
            )}

            {/* Counted by type, in a fixed order, each as soon as it answers; a click shows that type's answers. */}
            <div className="space-y-2">
              {groups.map((group) => {
                const isGroupOpen = openGroups.includes(group.type);
                return (
                  <section key={group.type} aria-label={knowledgeFoundLabel(group.type, group.results.length)}>
                    <button
                      type="button"
                      aria-expanded={isGroupOpen}
                      onClick={() => toggleGroup(group.type)}
                      className="flex w-full items-center justify-between rounded-card border border-hairline bg-surface px-3 py-2 text-left text-[13px] font-semibold text-ink transition-colors hover:border-signature hover:bg-signature-tint focus-visible:outline focus-visible:outline-2 focus-visible:outline-signature"
                    >
                      <span>{knowledgeFoundLabel(group.type, group.results.length)}</span>
                      <svg
                        aria-hidden="true"
                        className={`h-[11px] w-[11px] text-ink-muted transition-transform ${isGroupOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isGroupOpen && (
                      <div className="mt-2 space-y-2 pl-3">
                        {group.results.map((result) => (
                          <KnowledgeResultCard key={`${result.type}:${result._id}`} result={result} />
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>

            {/* The types still out, named in group order, under what has arrived. */}
            {pending.length > 0 && (
              <p className="mt-2 flex items-center gap-2 py-1 text-xs text-ink-secondary">
                <Spinner />
                {LIVE_KNOWLEDGE_SEARCH_COPY.searching(pending.map(({ many }) => many).join(', '))}
              </p>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default LiveKnowledgeSearch;
